import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Play, Mail, Clock, Webhook, X, Copy, RefreshCw, Loader2, CheckCircle2, Radio, Square, Save, Variable } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

const SUPABASE_URL = 'https://yqmlqctpakqxtwzrdofl.supabase.co';
const LISTEN_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export function TriggerSelectionModal({
  open,
  onOpenChange,
  onSelectTrigger,
  selectedTriggers,
  workflowId,
  webhookToken,
  onRegenerateToken,
  workflowSettings,
  onSaveWebhookVariables
}) {
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Listen mode state: 'idle' | 'listening' | 'received'
  const [listenState, setListenState] = useState('idle');
  const [receivedPayload, setReceivedPayload] = useState(null);
  const [flattenedVars, setFlattenedVars] = useState([]);
  const listenTimeoutRef = useRef(null);
  const channelRef = useRef(null);

  const triggers = [
    {
      id: 'manual',
      icon: Play,
      label: 'Manual',
      description: 'Start this workflow manually',
    },
    {
      id: 'schedule',
      icon: Clock,
      label: 'Schedule',
      description: 'Run on a scheduled basis',
    },
    {
      id: 'webhook',
      icon: Webhook,
      label: 'Webhook',
      description: 'Trigger via HTTP webhook',
    },
  ];

  const handleTriggerClick = (trigger) => {
    onSelectTrigger(trigger);
  };

  const webhookUrl = webhookToken
    ? `${SUPABASE_URL}/functions/v1/workflow-webhook?token=${webhookToken}`
    : null;
  const listenUrl = webhookToken
    ? `${SUPABASE_URL}/functions/v1/workflow-webhook?token=${webhookToken}&listen=true`
    : null;

  const savedWebhookVars = workflowSettings?.webhookVariables || [];

  // Cleanup on unmount or dialog close
  useEffect(() => {
    if (!open) {
      stopListening();
    }
    return () => {
      stopListening();
    };
  }, [open]);

  // Realtime subscription for listen mode
  useEffect(() => {
    if (listenState !== 'listening' || !webhookToken) return;

    const channel = supabase
      .channel(`webhook-listen-${webhookToken}`)
      .on('broadcast', { event: 'webhook_payload' }, (message) => {
        const payload = message.payload?.payload;
        if (payload) {
          setReceivedPayload(payload);
          // Flatten the payload into variables
          const flatVars = [];
          const flatten = (obj, prefix) => {
            if (obj === null || obj === undefined) return;
            if (Array.isArray(obj)) {
              obj.forEach((item, index) => flatten(item, `${prefix}.${index}`));
              if (obj.length > 0) flatVars.push({ key: prefix, sampleValue: String(obj[0]) });
              return;
            }
            if (typeof obj === 'object') {
              Object.entries(obj).forEach(([key, value]) => flatten(value, `${prefix}.${key}`));
              return;
            }
            flatVars.push({ key: prefix, sampleValue: String(obj) });
          };
          flatten(payload, 'webhook');
          setFlattenedVars(flatVars);
          setListenState('received');
          // Clear the timeout
          if (listenTimeoutRef.current) {
            clearTimeout(listenTimeoutRef.current);
            listenTimeoutRef.current = null;
          }
        }
      })
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [listenState, webhookToken]);

  const startListening = () => {
    setReceivedPayload(null);
    setFlattenedVars([]);
    setListenState('listening');

    // Set 5-minute timeout
    listenTimeoutRef.current = setTimeout(() => {
      toast.info('Listening timed out after 5 minutes. Try again when ready.');
      setListenState('idle');
    }, LISTEN_TIMEOUT_MS);
  };

  const stopListening = () => {
    setListenState('idle');
    if (listenTimeoutRef.current) {
      clearTimeout(listenTimeoutRef.current);
      listenTimeoutRef.current = null;
    }
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  };

  const handleCopyUrl = async (url) => {
    if (!url) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('URL copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy URL');
    }
  };

  const handleRegenerateToken = async () => {
    if (!workflowId || !onRegenerateToken) return;

    setIsRegenerating(true);
    try {
      const newToken = crypto.randomUUID();

      const { error } = await supabase
        .from('workflows')
        .update({ webhook_token: newToken })
        .eq('id', workflowId);

      if (error) throw error;

      onRegenerateToken(newToken);
      stopListening();
      toast.success('Webhook token regenerated. Update any integrations using the old URL.');
    } catch (error) {
      console.error('Failed to regenerate token:', error);
      toast.error('Failed to regenerate token');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleSaveVariables = () => {
    if (onSaveWebhookVariables && flattenedVars.length > 0) {
      onSaveWebhookVariables(flattenedVars);
      toast.success(`Saved ${flattenedVars.length} webhook variables`);
    }
  };

  const handleCopyVariable = (varKey) => {
    const text = `{{ ${varKey} }}`;
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${text}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            Select a Trigger to Run this Workflow
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 overflow-y-auto min-h-0">
          {/* Selected Triggers */}
          {selectedTriggers.length > 0 && (
            <div className="mb-4 space-y-2">
              {selectedTriggers.map((trigger) => {
                const Icon = triggers.find(t => t.id === trigger.id)?.icon || Play;
                return (
                  <div key={trigger.id}>
                    <div className="flex items-center justify-between px-4 py-2 border rounded-lg bg-muted/50">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        <span className="font-medium">{trigger.label}</span>
                      </div>
                      <button
                        onClick={() => onSelectTrigger(trigger)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Webhook Configuration Panel */}
                    {trigger.id === 'webhook' && webhookToken && (
                      <div className="mt-2 p-4 border rounded-lg bg-card space-y-4 min-w-0 overflow-hidden">
                        {/* Webhook URL */}
                        <div>
                          <label className="text-sm font-medium text-muted-foreground mb-1.5 block">
                            Webhook URL
                          </label>
                          <div className="flex gap-2">
                            <Input
                              value={webhookUrl}
                              readOnly
                              className="font-mono text-xs bg-muted"
                            />
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleCopyUrl(webhookUrl)}
                              title="Copy URL"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1.5">
                            Send a POST request to this URL to trigger the workflow
                          </p>
                        </div>

                        {/* === IDLE STATE === */}
                        {listenState === 'idle' && (
                          <>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={startListening}
                              >
                                <Radio className="h-4 w-4 mr-2" />
                                Listen for Events
                              </Button>

                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleRegenerateToken}
                                disabled={isRegenerating}
                                className="text-muted-foreground"
                              >
                                {isRegenerating ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                )}
                                Regenerate Token
                              </Button>
                            </div>

                            {/* Saved Variables Summary */}
                            {savedWebhookVars.length > 0 && (
                              <div className="p-3 rounded-lg bg-muted/50 border">
                                <div className="flex items-center gap-1.5 mb-2">
                                  <Variable className="h-3.5 w-3.5 text-muted-foreground" />
                                  <span className="text-xs font-medium text-muted-foreground">
                                    Saved Webhook Variables ({savedWebhookVars.length})
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {savedWebhookVars.map((v) => (
                                    <Badge
                                      key={v.key}
                                      variant="secondary"
                                      className="text-[10px] font-mono cursor-pointer hover:bg-primary/20"
                                      onClick={() => handleCopyVariable(v.key)}
                                    >
                                      {`{{ ${v.key} }}`}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* === LISTENING STATE === */}
                        {listenState === 'listening' && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                              <div className="relative flex-shrink-0">
                                <span className="flex h-3 w-3">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                                </span>
                              </div>
                              <span className="text-sm font-medium text-blue-700">
                                Listening for incoming webhook...
                              </span>
                            </div>

                            <div className="text-xs text-muted-foreground space-y-1.5 pl-1">
                              <p className="font-medium">Steps:</p>
                              <ol className="list-decimal list-inside space-y-1 text-[11px]">
                                <li>Copy the test URL below</li>
                                <li>Send a POST request from your external system (e.g. curl, Postman, Zapier)</li>
                                <li>The payload will appear here automatically</li>
                              </ol>
                            </div>

                            <div>
                              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                Test URL (use this while listening)
                              </label>
                              <div className="flex gap-2">
                                <Input
                                  value={listenUrl}
                                  readOnly
                                  className="font-mono text-[10px] bg-muted"
                                />
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleCopyUrl(listenUrl)}
                                  title="Copy test URL"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={stopListening}
                            >
                              <Square className="h-3.5 w-3.5 mr-2" />
                              Stop Listening
                            </Button>
                          </div>
                        )}

                        {/* === PAYLOAD RECEIVED STATE === */}
                        {listenState === 'received' && receivedPayload && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 border border-green-200">
                              <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                              <span className="text-sm font-medium text-green-700">
                                Payload received!
                              </span>
                            </div>

                            {/* Raw JSON */}
                            <div className="min-w-0">
                              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                                Raw Payload
                              </label>
                              <pre className="p-3 rounded-lg text-[10px] font-mono overflow-auto max-h-[120px] whitespace-pre-wrap break-all bg-muted border">
                                {JSON.stringify(receivedPayload, null, 2)}
                              </pre>
                            </div>

                            {/* Flattened Variables */}
                            {flattenedVars.length > 0 && (
                              <div className="p-3 rounded-lg bg-muted/50 border min-w-0">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                                    <Variable className="h-3 w-3" />
                                    Available Variables ({flattenedVars.length})
                                  </p>
                                </div>
                                <div className="space-y-0.5 max-h-[140px] overflow-y-auto">
                                  {flattenedVars.map((v) => (
                                    <button
                                      key={v.key}
                                      onClick={() => handleCopyVariable(v.key)}
                                      className="w-full flex items-center gap-1.5 px-2 py-1 text-left rounded hover:bg-muted transition-colors group min-w-0 overflow-hidden"
                                    >
                                      <code className="text-[10px] font-mono text-primary truncate max-w-[45%] flex-shrink-0">{`{{ ${v.key} }}`}</code>
                                      <span className="text-[10px] text-muted-foreground flex-shrink-0">&rarr;</span>
                                      <span className="text-[10px] text-foreground truncate flex-1 min-w-0">{v.sampleValue}</span>
                                      <Copy className="h-2.5 w-2.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Actions */}
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                onClick={handleSaveVariables}
                                disabled={flattenedVars.length === 0}
                              >
                                <Save className="h-4 w-4 mr-2" />
                                Save as Variables
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={startListening}
                              >
                                <Radio className="h-4 w-4 mr-2" />
                                Listen Again
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Available Triggers */}
          <div className="space-y-2">
            {triggers
              .filter(trigger => !selectedTriggers.find(t => t.id === trigger.id))
              .map((trigger) => {
                const Icon = trigger.icon;
                return (
                  <button
                    key={trigger.id}
                    onClick={() => handleTriggerClick(trigger)}
                    className="w-full flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors text-left"
                  >
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium mb-1">{trigger.label}</div>
                      <div className="text-sm text-muted-foreground">
                        {trigger.description}
                      </div>
                    </div>
                  </button>
                );
              })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
